<template>
  <div>
    <Head :title="client.name" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/clients" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Clientes
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>{{ client.name }}</span>
        </h1>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <!-- Estado de Cuenta PDF -->
        <a
          :href="`/clients/${client.id}/statement`"
          class="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-file-pdf text-red-600" />
          Estado de Cuenta
        </a>

        <!-- Eliminar Cliente -->
        <button
          type="button"
          class="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm border border-red-200"
          @click="confirmDeleteClient"
        >
          <i class="fa fa-trash" />
          Eliminar Cliente
        </button>

        <!-- Editar Cliente -->
        <Link
          :href="`/clients/${client.id}/edit`"
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-pen" />
          Editar Cliente
        </Link>
      </div>
    </div>

    <!-- Información del Cliente -->
    <div class="bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Información Personal -->
          <div>
            <h2 class="text-lg font-semibold text-gray-800 mb-4">Información Personal</h2>
            <dl class="grid grid-cols-1 gap-4">
              <div>
                <dt class="text-sm font-medium text-gray-500">Nombre</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.name }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500">Identificación</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.identification }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500">Correo Electrónico</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.email }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500">Teléfono</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.phone }}</dd>
              </div>
            </dl>
          </div>

          <!-- Información de Empresa -->
          <div>
            <h2 class="text-lg font-semibold text-gray-800 mb-4">Información de Empresa</h2>
            <dl class="grid grid-cols-1 gap-4">
              <div>
                <dt class="text-sm font-medium text-gray-500">Nombre de Empresa</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.companyName }}</dd>
              </div>
              <div>
                <dt class="text-sm font-medium text-gray-500">Dirección</dt>
                <dd class="mt-1 text-sm text-gray-900">{{ client.address }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <!-- Estado de Cuenta / KPIs -->
    <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Balance</h3>
        <p class="text-3xl font-bold" :class="balance >= 0 ? 'text-green-600' : 'text-red-600'">
          {{ formatCurrency(balance) }}
        </p>
      </div>
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Deuda</h3>
        <p class="text-3xl font-bold" :class="balanceTotal >= 0 ? 'text-red-600' : 'text-green-600'">
          {{ formatCurrency(balanceTotal) }}
        </p>
      </div>
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Total ventas</h3>
        <p class="text-3xl font-bold text-blue-600">{{ totalOrders }}</p>
      </div>
      <div class="bg-white rounded-lg shadow-sm p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">Total Gastado</h3>
        <p class="text-3xl font-bold text-green-600">{{ formatCurrency(totalSpent) }}</p>
      </div>
    </div>

    <!-- Historial de Órdenes -->
    <div class="mt-6 bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Historial de Ventas</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"># Orden</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsable</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="order in orders.data" :key="order.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <Link :href="`/orders/${order.id}`" class="text-blue-600 hover:text-blue-900">
                    #{{ order.id }}
                  </Link>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(order.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="getOrderStateClass(order.order_state)"
                  >
                    {{ getOrderStateLabel(order.order_state) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatCurrency(order.total) }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm"
                  :class="order.balance >= 0 ? 'text-green-600' : 'text-red-600'"
                >
                  {{ formatCurrency(order.balance) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ order.responsible ? `${order.responsible.first_name} ${order.responsible.last_name}` : '-' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <pagination 
          v-if="orders && orders.links" 
          :links="orders.links" 
          class="mt-6"
        />
      </div>
    </div>

    <!-- Componente de registrar pago -->
    <PaymentRegister
      v-if="client"
      class="mt-6"
      :payment_methods="payment_methods"
      :balance="client.balance"
      :change="change"
      :order="{ total: 0, balance: 0 }"
      :client="client"
      @success="refreshData"
    />

    <!-- Historial de Pagos -->
    <div class="mt-6 bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Historial de Pagos</h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"># Pago</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Método</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referencia</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="payment in (payments?.data || [])" :key="payment.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <Link :href="`/payments/${payment.id}`" class="text-blue-600 hover:text-blue-900">
                    #{{ payment.id }}
                  </Link>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(payment.created_at) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ payment.method }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ payment.reference }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatCurrency(payment.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <pagination 
          v-if="payments && payments.links" 
          :links="payments.links" 
          class="mt-6"
        />
      </div>
    </div>

    <!-- Productos comprados (por rango de fecha) -->
    <div class="mt-6 bg-white rounded-lg shadow-sm overflow-hidden">
      <div class="p-6">
        <div class="flex items-end justify-between flex-wrap gap-4 mb-4">
          <h2 class="text-lg font-semibold text-gray-800">Productos comprados</h2>
          <div class="flex items-end gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Desde</label>
              <input
                v-model="fromDate"
                type="date"
                class="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
              <input
                v-model="toDate"
                type="date"
                class="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              @click="fetchPurchasedProducts"
            >
              Buscar
            </button>
            <a
              :href="exportPdfUrl"
              target="_blank"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Exportar PDF
            </a>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orden</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-if="!purchasedProducts || (purchasedProducts.data && purchasedProducts.data.length === 0)">
                <td class="px-6 py-4 text-sm text-gray-500" colspan="5">Sin resultados para el rango seleccionado.</td>
              </tr>
              <tr v-for="item in (purchasedProducts?.data || [])" :key="item.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ item.name || item.product_name || '-' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ item.quantity ?? '-' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatCurrency(item.price || item.unit_price || 0) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatDate(item.created_at) }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  <Link v-if="item.order_id" :href="`/orders/${item.order_id}`">#{{ item.order_id }}</Link>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <pagination 
          v-if="purchasedProducts && purchasedProducts.links" 
          :links="purchasedProducts.links" 
          class="mt-6"
        />
      </div>
    </div>

    <!-- Configuraciones especiales-->
    <div v-if="canDeleteHistory" class="flex justify-end space-x-4 mt-4">
      <button 
        class="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        @click="confirmDeleteHistory"
      >
        <i class="fa fa-trash" />
        Borrar historial de cliente
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import AppToolbar from '@/Shared/AppToolbar.vue'
import { formatDate, formatCurrency } from '@/utils/format'
import { getOrderStateClass, getOrderStateLabel } from '@/utils/orderStates'
import PaymentRegister from '@/Components/PaymentRegister.vue'

export default {
  name: 'ClientsShow',

  components: {
    Head,
    Link,
    Pagination,
    AppToolbar,
    PaymentRegister,
  },

  layout: Layout,

  props: {
    client: Object,
    balanceTotal: Number,
    orders: Object,
    totalOrders: Number,
    totalSpent: Number,
    balance: Number,
    payments: Object,
    auth: Object,
    contact: Object,
    payment_methods: Array,
    change: Number,
    purchasedProducts: Object,
  },

  data() {
    return {
      fromDate: '',
      toDate: '',
    }
  },

  computed: {
    canDeleteHistory() {
      const user = this.$page?.props?.auth?.user || this.auth?.user
      if (!user) return false
      return user.owner || user.role === 'admin' || user.role === 'financial' || user.role === 'superadmin'
    },
    exportPdfUrl() {
      const params = new URLSearchParams()
      if (this.fromDate) params.append('from', this.fromDate)
      if (this.toDate) params.append('to', this.toDate)
      const query = params.toString()
      return `/clients/${this.client.id}/purchased-products/pdf` + (query ? `?${query}` : '')
    },
  },

  methods: {
    formatDate,
    formatCurrency,
    getOrderStateClass,
    getOrderStateLabel,
    confirmDeleteClient() {
      if (confirm('¿Está seguro que desea eliminar este cliente? Se eliminarán también sus órdenes asociadas y los cambios no se pueden revertir.')) {
        this.$inertia.delete(`/clients/${this.client.id}`)
      }
    },
    confirmDeleteHistory() {
      if (confirm('¿Estás seguro de querer borrar el historial de cliente?, los cambios no se pueden revertir.')) {
        this.deleteHistory()
      }
    },
    deleteHistory() {
      this.$inertia.delete(`/clients/${this.client.id}/delete-history`)
    },
    refreshData() {
      this.$inertia.reload({ preserveScroll: true })
    },
    fetchPurchasedProducts() {
      const params = {}
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      this.$inertia.get(`/clients/${this.client.id}`, params, {
        preserveState: true,
        replace: true,
        only: ['client', 'purchasedProducts', 'payments', 'orders', 'balance', 'balanceTotal', 'totalOrders', 'totalSpent', 'payment_methods', 'change', 'auth'],
      })
    },
  },
}
</script>
